import React, { useContext } from 'react';
import {
  Formik,
  FormikConfig,
  FormikValues,
  useField as useFormikField,
} from 'formik';
import { reach, Schema } from 'yup';

const FormContext = React.createContext<{
  validationSchema: Schema<any>;
  onChange?: (event: React.ChangeEvent) => void;
}>({ validationSchema: {} as Schema<any> });

/**
 * Extends Formik component to include validationSchema (otherwise not accessible) and global onChange handler.
 * ValidationSchema is used in Field components to check if a value is required (to set aria-required)
 * and retrieve label (if it's defined).
 *
 * Use instead of Formik. Accepts the same props.
 */
export const FormContextProvider = <Values extends FormikValues>(
  props: FormikConfig<Values> & {
    onChange?: (event: React.ChangeEvent) => void;
  }
) => {
  const { validationSchema, onChange } = props;

  return (
    <FormContext.Provider value={{ validationSchema, onChange }}>
      <Formik<Values> {...props} />
    </FormContext.Provider>
  );
};

const useFormContext = () => useContext(FormContext);

/**
 * Extends useField from formik to enable providing form-level onChange handlers.
 */
export const useField: typeof useFormikField = (...args) => {
  const { onChange: formOnChange } = useFormContext();
  const [{ onChange, ...field }, meta, helpers] = useFormikField(...args);
  const customOnChange = (event: React.ChangeEvent) => {
    onChange(event);

    if (formOnChange) {
      formOnChange(event);
    }
  };

  return [
    {
      onChange: customOnChange,
      ...field,
    },
    meta,
    helpers,
  ];
};

export const useFieldDescription: (name: string) => {
  fieldLabel?: string;
  required?: boolean;
} = (name) => {
  const { validationSchema } = useFormContext();
  if (!validationSchema) {
    return {};
  }

  const fieldDescription = reach(validationSchema, name).describe();

  return {
    fieldLabel: fieldDescription.label,
    required: fieldDescription.tests.some(({ name }) => name === 'required'),
  };
};

export const useCharactersCount: (name: string) => { max?: number } = (
  name
) => {
  const { validationSchema } = useFormContext();

  if (!validationSchema) {
    return {};
  }

  const fieldDescription = reach(validationSchema, name).describe();

  const maxWords = fieldDescription.tests.find((test) => test.name === 'max');
  const minWords = fieldDescription.tests.find((test) => test.name === 'min');

  return {
    max: maxWords?.params.max,
    min: minWords?.params.min,
  };
};
